import {useState} from 'react';
import {Table, Pagination, Tag, Button} from 'antd';
import {MenuOutlined} from '@ant-design/icons';
import {useMutation, useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import moment from 'moment';
import {useNavigate} from 'react-router-dom';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import {arrayMoveImmutable} from 'array-move';
import {
  GET_ALL_QUICK_ACTIONS_CATEGORIES,
  DELETE_QUICK_ACTION_CATEGORY,
  REORDER_QUICK_ACTION_CATEGORIES,
} from './resolvers';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import styles from './quickActions.module.scss';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import {useSelector} from 'react-redux';

const DragHandle = SortableHandle(({className}) => (
  <div className={className}>
    <MenuOutlined />
  </div>
));

const SortableItem = SortableElement(props => <tr {...props} />);
const SortableBody = SortableContainer(props => <tbody {...props} />);

const QuickActionCategories = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    categoryId: null,
  });
  const [pendingReorder, setPendingReorder] = useState(null);
  const [reorderModal, setReorderModal] = useState(false);
  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.QuickActions),
  );

  const {data, loading, refetch} = useQuery(GET_ALL_QUICK_ACTIONS_CATEGORIES, {
    variables: {
      activeOnly: false,
    },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    onError: error => toast.error(error.message),
  });

  const [deleteCategory, {loading: deleting}] = useMutation(
    DELETE_QUICK_ACTION_CATEGORY,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [reorderCategories, {loading: reordering}] = useMutation(
    REORDER_QUICK_ACTION_CATEGORIES,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const originalCategories =
    data?.getAllQuickActionCategories?.__typename ===
    'ResponseWithQuickActionCategories'
      ? data?.getAllQuickActionCategories?.data || []
      : [];

  // Use pending reorder if available, otherwise use original categories
  const categories = pendingReorder || originalCategories;

  const pagination =
    data?.getAllQuickActionCategories?.__typename ===
    'ResponseWithQuickActionCategories'
      ? data?.getAllQuickActionCategories?.pagination || {}
      : {};

  const onSortEnd = ({oldIndex, newIndex}) => {
    if (oldIndex === newIndex) return;

    // Get the current list (either pending reorder or original)
    const currentList = pendingReorder || originalCategories;
    const reorderedCategories = arrayMoveImmutable(
      currentList.slice(),
      oldIndex,
      newIndex,
    );

    // Update pending reorder state
    setPendingReorder(reorderedCategories);
  };

  const handleConfirmReorder = () => {
    if (!pendingReorder) return;

    const positions = pendingReorder.map((category, index) => ({
      id: category.id,
      position: index + 1,
    }));

    reorderCategories({
      variables: {
        positions,
      },
    })
      .then(({data: {reorderQuickActionCategories}}) => {
        if (
          reorderQuickActionCategories.__typename === 'Error' ||
          reorderQuickActionCategories.statusCode === 400
        ) {
          toast.error(reorderQuickActionCategories.message);
        } else {
          toast.success('Categories reordered successfully.');
          setPendingReorder(null);
          setReorderModal(false);
          refetch();
        }
      })
      .catch(error => {
        toast.error(error.message);
      });
  };

  const handleCancelReorder = () => {
    setPendingReorder(null);
    setReorderModal(false);
  };

  const DraggableContainer = props => (
    <SortableBody
      useDragHandle
      disableAutoscroll
      onSortEnd={onSortEnd}
      {...props}
    />
  );

  const DraggableBodyRow = ({...restProps}) => {
    const index = categories.findIndex(x => x.id === restProps['data-row-key']);
    return <SortableItem index={index} {...restProps} />;
  };

  const columns = [
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
      width: 120,
      render: (_, record, index) => (
        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
          <DragHandle className={styles.dragHandle} />
          <span>{pendingReorder ? index + 1 : record.position}</span>
        </div>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
    },
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
      render: language => <Tag>{language || '—'}</Tag>,
    },

    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: isActive => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: date => (date ? moment(date).format('DD MMM YYYY HH:mm') : '—'),
    },
    {
      title: 'Actions',
      fixed: 'right',
      key: 'actions',
      render: (_, record) => (
        <div style={{display: 'flex', gap: '8px'}}>
          <Button
            type="link"
            disabled={!tabOperations?.update}
            onClick={() =>
              navigate(`/dashboard/quick-actions/categories/${record.id}/edit`)
            }>
            Edit
          </Button>
          <Button
            type="link"
            danger
            disabled={!tabOperations?.delete}
            onClick={() => setDeleteModal({open: true, categoryId: record.id})}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  const handlePageChange = page => {
    setCurrentPage(page);
    // Note: The query doesn't currently support pagination variables
    // You may need to update the query to accept page and limit
  };

  const handleLimitChange = size => {
    setLimit(size);
    setCurrentPage(1);
    // Note: The query doesn't currently support pagination variables
    // You may need to update the query to accept page and limit
  };

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeader}>
        <h2>Quick Action Categories</h2>
        <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
          {pendingReorder && (
            <>
              <Button
                type="primary"
                onClick={() => setReorderModal(true)}
                style={{background: '#52c41a', borderColor: '#52c41a'}}
                disabled={!tabOperations?.update}>
                Save Order Changes
              </Button>
              <Button onClick={handleCancelReorder}>Discard Changes</Button>
            </>
          )}
          <Button
            type="primary"
            disabled={!tabOperations?.create}
            onClick={() => navigate('/dashboard/quick-actions/categories/new')}>
            Create Category
          </Button>
        </div>
      </div>
      <Table
        className="antd-table"
        dataSource={categories}
        columns={columns}
        loading={loading || reordering}
        rowKey="id"
        pagination={false}
        components={{
          body: {
            wrapper: DraggableContainer,
            row: DraggableBodyRow,
          },
        }}
      />
      {pagination.total > 0 && (
        <Pagination
          current={currentPage}
          onChange={handlePageChange}
          total={pagination.total}
          showQuickJumper
          className="ant-table-pagination"
          showSizeChanger
          pageSize={limit}
          onShowSizeChange={(_, size) => handleLimitChange(size)}
        />
      )}
      <ConfirmModal
        openModal={deleteModal.open}
        setOpenModal={() => setDeleteModal({open: false, categoryId: null})}
        loading={deleting}
        title="Delete Category"
        confirmText="Are you sure you want to delete this category? This action cannot be undone!"
        handleOperation={() =>
          deleteCategory({
            variables: {
              categoryId: deleteModal.categoryId,
            },
          })
            .then(({data: {deleteQuickActionCategory}}) => {
              if (
                deleteQuickActionCategory.__typename === 'Error' ||
                deleteQuickActionCategory.statusCode === 400
              ) {
                toast.error(deleteQuickActionCategory.message);
              } else {
                toast.success('Category deleted successfully.');
                refetch();
                setDeleteModal({open: false, categoryId: null});
              }
            })
            .catch(error => {
              toast.error(error.message);
            })
        }
      />
      <ConfirmModal
        openModal={reorderModal}
        setOpenModal={setReorderModal}
        loading={reordering}
        title="Confirm Reorder"
        confirmText="Are you sure you want to save the new order of categories?"
        handleOperation={handleConfirmReorder}
      />
    </div>
  );
};

export default QuickActionCategories;
